import { useContext, useState } from "react";
import { useLocation } from "react-router-dom";

import {
  Container,
  Text,
  TextInput,
  Button,
  Textarea,
  Select,
  TextInputProps,
  SelectProps,
  InputProps,
} from "@mantine/core";
import { useForm } from "@mantine/form";

import { AppName, Colors, SupportCategories } from "../../Lib/Data";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getEllipsisFromString,
  trimString,
} from "../../Lib/Methods";
import { Endpoints } from "../../Lib/Endpoints";
import { PerformRequest } from "../../Lib/usePerformRequest";

import "./styles.scss";
import WhatsappChatBanner from "../../Components/WhatsAppChatBanner";
import { AppContext } from "../Wrapper";

const CreateSupport: React.FC = () => {
  const location = useLocation();
  const context = useContext(AppContext);
  const theme = context?.theme ?? "light";

  document.title = `Contact Support | ${AppName}`;

  // Check for search params: sub and transaction
  const searchParams = new URLSearchParams(location.search);
  const subscription = searchParams.get("subscription");
  const transaction = searchParams.get("transaction");

  const dispute: "transaction" | "subscription" | null = subscription
    ? "subscription"
    : transaction
    ? "transaction"
    : null;

  const supportForm = useForm({
    initialValues: {
      title: "",
      category: "general-support",
      message: "",
    },
    validate: {
      title: (value) =>
        value && value.length > 0 ? null : "Enter message title",
      category: (value) =>
        value && value.length > 0 ? null : "Select category",
      message: (value) =>
        value && value.length > 0 ? null : "You must enter a message",
    },
  });

  const [isCreatingRequest, setCreatingRequest] = useState<boolean>(false);
  const CreateSupportRequest = async () => {
    const { hasErrors } = supportForm.validate();
    if (!hasErrors) {
      setCreatingRequest(true);
      const { title, category, message } = supportForm.values;
      const data = {
        title: trimString(title),
        category,
        message: trimString(getEllipsisFromString(message, 1000)),
        dispute,
        subscriptionID: subscription,
        transactionID: transaction,
      };
      const r = await PerformRequest({
        method: "POST",
        route: Endpoints.GetCreateNewTicket(),
        data,
        callback: () => {
          setCreatingRequest(false);
        },
      });
      setCreatingRequest(false);
      if (r) {
        if (r.status === 201) {
          DefaultSuccessNotification("We have received your message!");
          supportForm.reset();
        } else {
          DefaultErrorNotification(
            "Could not create request! Please try again"
          );
        }
      } else {
        DefaultErrorNotification("Could not create request! Please try again");
      }
    }
  };

  const inputProps = {
    spellCheck: false,
    disabled: !context || !context.profile || !context.profile.id,
    styles: {
      input: {
        backgroundColor: theme === "dark" ? Colors.BlackPrimary : Colors.White,
        color: theme === "dark" ? Colors.White : Colors.BlackPrimary,
        borderColor:
          theme === "dark" ? Colors.BlackPrimary : Colors.GrayTertiary,
        "&::placeholder": {
          color: theme === "dark" ? Colors.White : Colors.GrayTertiary,
        },
      },
      label: {
        color: theme === "dark" ? Colors.White : Colors.BlackPrimary,
      },
    },
  };
  return (
    <Container size="md">
      <div className="create-support-container">
        <Text
          fw={500}
          mb={30}
          c={theme === "dark" ? Colors.White : Colors.GreenSecondary}
        >
          New Support Ticket
        </Text>
        {context && (
          <>
            {!context.profile?.id && (
              <Text mt={-30} mb={30} c={Colors.BluePrimary}>
                You must be logged in to log a support request!
              </Text>
            )}
          </>
        )}
        {dispute && (
          <div className="flex-col">
            <Text fw={500} tt="capitalize" fz="sm">
              {dispute}
            </Text>
            <Text
              fw={500}
              tt="capitalize"
              fz="lg"
              c={Colors.GreenPrimary}
              mt={2}
              mb={30}
            >
              {dispute === "transaction" ? transaction : subscription}
            </Text>
          </div>
        )}
        <div className="content flex-row justify-between width-100">
          <div className="form">
            <div className="flex-row align-center row justify-between width-100">
              <TextInput
                {...inputProps}
                placeholder="E.g. Invalid login information"
                className="input"
                label="Title"
                {...supportForm.getInputProps("title")}
              />
              <Select
                {...inputProps}
                data={SupportCategories}
                allowDeselect={false}
                className="input"
                label="Category"
                {...supportForm.getInputProps("category")}
              />
            </div>
            <Textarea
              {...inputProps}
              w="100%"
              rows={5}
              mt={20}
              label="Message"
              placeholder="Enter message..."
              {...supportForm.getInputProps("message")}
            />
            <Button
              disabled={inputProps.disabled}
              mt={20}
              w={120}
              onClick={CreateSupportRequest}
              loading={isCreatingRequest}
              color={Colors.GreenPrimary}
            >
              Submit
            </Button>
          </div>
        </div>
        <WhatsappChatBanner />
      </div>
    </Container>
  );
};

export default CreateSupport;
